import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CoverageAreaProps, LogisticsInitialStore } from "./types.store";
// eslint-disable-next-line import/no-cycle
import {
  handleGetWorkingAreasRequest,
  handleUpdateWorkingAreaStatusRequest,
  handleUpdateWorkingAreaRequest,
  handleUpdateWorkingHourRequest,
  handleDeleteWorkingAreaRequest,
  handleGetWorkingAreaDetailRequest,
} from "./thunk.store";

const initialState: LogisticsInitialStore = {
  workingAreas: null,
};

const LogisticsStore = createSlice({
  initialState,
  name: "logisticsData",
  reducers: {
    patchCoverageAreaDrivers: (
      state,
      action: PayloadAction<{
        coverageAreaId: string;
        patch: Partial<Pick<CoverageAreaProps, "drivers" | "hasDriver">>;
      }>,
    ) => {
      if (!state.workingAreas) {
        return;
      }
      const index = state.workingAreas.findIndex(
        (area) => area.id === action.payload.coverageAreaId,
      );
      if (index === -1) {
        return;
      }
      state.workingAreas[index] = {
        ...state.workingAreas[index],
        ...action.payload.patch,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(handleGetWorkingAreasRequest.fulfilled, (state, action) => {
      const previousById = new Map(
        (state.workingAreas ?? []).map((area) => [area.id, area]),
      );

      state.workingAreas = action.payload.map((summary) => {
        const previous = previousById.get(summary.id);
        if (!previous) {
          return summary;
        }

        const hasDetailedData =
          (previous.openingHours?.length ?? 0) > 0 ||
          Boolean(previous.storeUri) ||
          Boolean(previous.map?.link) ||
          Boolean(
            previous.map?.geoJson &&
              Object.keys(previous.map.geoJson as object).length > 0,
          );

        return hasDetailedData ? previous : summary;
      });
    });
    builder.addCase(
      handleGetWorkingAreaDetailRequest.fulfilled,
      (state, action) => {
        if (!state.workingAreas) {
          state.workingAreas = [action.payload];
          return;
        }
        const index = state.workingAreas.findIndex(
          (area) => area.id === action.payload.id,
        );
        if (index === -1) {
          state.workingAreas.push(action.payload);
          return;
        }
        state.workingAreas[index] = action.payload;
      },
    );
    builder.addCase(
      handleUpdateWorkingAreaStatusRequest.fulfilled,
      (state, action) => {
        state.workingAreas =
          state.workingAreas?.map((workingArea) => {
            if (workingArea.id === action.payload.id) {
              return { ...workingArea, enabled: action.payload.enabled };
            }

            return workingArea;
          }) || null;
      },
    );
    builder.addCase(
      handleUpdateWorkingAreaRequest.fulfilled,
      (state, action) => {
        state.workingAreas =
          state.workingAreas?.map((workingArea) => {
            if (workingArea.id === action.payload.id) {
              return action.payload;
            }

            return workingArea;
          }) || null;
      },
    );
    builder.addCase(
      handleUpdateWorkingHourRequest.fulfilled,
      (state, action) => {
        state.workingAreas =
          state.workingAreas?.map((workingArea) => {
            if (workingArea.id === action.payload.coverageAreaId) {
              return {
                ...workingArea,
                openingHours: action.payload.openingHours,
              };
            }

            return workingArea;
          }) || null;
      },
    );
    builder.addCase(
      handleDeleteWorkingAreaRequest.fulfilled,
      (state, action) => {
        state.workingAreas =
          state.workingAreas?.filter(
            (workingArea) => workingArea.id !== action.payload.id,
          ) || null;
      },
    );
  },
});

export const { patchCoverageAreaDrivers } = LogisticsStore.actions;

export default LogisticsStore.reducer;
